import React, { useMemo } from "react";
import { StarOfIshtar, StarGlyph, Ornament, ZigguratDivider, BeeGlyph } from "../Ornaments";
import { WingedBird, ZigguratHorizon } from "../MythicMotifs";

const paletteFor = (roll) => {
  if (roll > 0.78) return "violet";
  if (roll > 0.55) return "lapis";
  return "gold";
};

const Particles = ({ count = 22 }) => {
  const items = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => {
        const left = Math.random() * 100;
        const delay = -Math.random() * 30;
        const dur = 24 + Math.random() * 26;
        const size = 1 + Math.random() * 1.8;
        const palette = paletteFor(Math.random());
        return { left, delay, dur, size, palette, i };
      }),
    [count]
  );
  const colorFor = (p) => {
    if (p === "violet") return { bg: "#8a5aa8", glow: "0 0 10px #8a5aa8" };
    if (p === "lapis") return { bg: "#3d6aa8", glow: "0 0 8px #3d6aa8" };
    return { bg: "#C9A84C", glow: "0 0 6px #C9A84C" };
  };
  return (
    <div className="particles" aria-hidden="true" data-testid="hero-particles">
      {items.map((p) => {
        const c = colorFor(p.palette);
        return (
          <span
            key={p.i}
            className="particle"
            style={{
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              background: c.bg,
              boxShadow: c.glow,
              animationDuration: `${p.dur}s`,
              animationDelay: `${p.delay}s`,
            }}
          />
        );
      })}
    </div>
  );
};

export const Hero = () => {
  return (
    <section
      id="top"
      className="section"
      data-testid="hero-section"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        paddingTop: "8rem",
        paddingBottom: "5rem",
        position: "relative",
        background: "linear-gradient(180deg, #0a0806 0%, #100c08 100%)",
        overflow: "hidden",
      }}
    >
      <div className="hero-grid-bg" />
      <Particles count={26} />
      <div className="hero-horizon" />
      {/* Violet aurora threading through lapis horizon */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          bottom: 0,
          left: "10%",
          right: "10%",
          height: "40vh",
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(138, 90, 168, 0.28) 0%, rgba(74, 42, 90, 0.10) 45%, transparent 75%)",
          filter: "blur(8px)",
          pointerEvents: "none",
        }}
        className="pulse-glow"
      />
      {/* Ziggurat city skyline */}
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 140, opacity: 0.55, pointerEvents: "none" }} aria-hidden="true">
        <ZigguratHorizon height={140} color="#1a3a6a" />
      </div>
      {/* Soaring birds */}
      <div style={{ position: "absolute", top: "18%", left: "8%", pointerEvents: "none" }} aria-hidden="true" className="pulse-glow">
        <WingedBird size={120} opacity={0.22} />
      </div>
      <div style={{ position: "absolute", top: "22%", right: "10%", pointerEvents: "none" }} aria-hidden="true" className="pulse-glow">
        <WingedBird size={140} opacity={0.20} flipped />
      </div>
      <div className="star-watermark spin-slow">
        <StarOfIshtar size={620} opacity={0.07} color="#C9A84C" glow="#E8C97A" />
      </div>

      {/* Honey pour — real photographic honey stream + rippling pool, screen-blended
          so the black background drops out cleanly. Tucked to the left edge. */}
      <img
        src="/media/honey-pour.jpg"
        alt=""
        aria-hidden="true"
        data-testid="hero-honey-pour"
        className="hero-honey-pour"
      />
      {/* Single bee — fixed, off to the side. No animation. */}
      <img
        src="/media/bee-2.png"
        alt=""
        aria-hidden="true"
        data-testid="hero-bee"
        className="hero-bee-static"
      />

      <div className="section-inner" style={{ position: "relative", zIndex: 2 }}>
        <div className="fade-up delay-100">
          <Ornament centered />
        </div>
        <p
          className="hero-sub fade-up delay-200"
          data-testid="hero-eyebrow"
          style={{ color: "var(--text-muted)" }}
        >
          Written in the body before it was written in stone
        </p>

        <h1 className="hero-title gold-text fade-up delay-300" data-testid="hero-title">
          Somatic Equilibrium
        </h1>

        <p
          className="font-body fade-up delay-400"
          data-testid="hero-subhead"
          style={{
            fontStyle: "italic",
            color: "var(--gold-bright)",
            fontSize: "clamp(1.05rem, 1.8vw, 1.35rem)",
            letterSpacing: "0.05em",
            marginBottom: "2.5rem",
          }}
        >
          The oldest human story is a somatic one.
        </p>

        <blockquote className="hero-quote fade-up delay-500" data-testid="hero-quote">
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
            <StarGlyph size={14} />
            <BeeGlyph size={18} />
            <StarGlyph size={14} />
          </span>
          <span style={{ display: "block", marginTop: "1rem" }}>
            Through intentional movement, we can observe and work with the body&rsquo;s motion data.
            By doing so, we cultivate a <em style={{ color: "var(--gold-bright)" }}>somatic signature</em>
            &nbsp;— a unique pattern of embodied organization. Through playful exploration of movement
            and equilibrium, this signature becomes more coherent, aligning both internal biological
            processes and external relational presence.
          </span>
        </blockquote>

        <div className="fade-up delay-600" style={{ display: "flex", justifyContent: "center", gap: "1rem", flexWrap: "wrap" }}>
          <a
            href="https://movethrus.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-angled"
            data-testid="hero-cta-enter"
            aria-label="Enter the practice — visit MOVE THRUS"
          >
            Enter the Practice
            <StarGlyph size={11} color="#0a0806" />
          </a>
        </div>

        <div className="fade-up delay-700" style={{ marginTop: "5rem", display: "flex", justifyContent: "center" }}>
          <ZigguratDivider width={240} height={28} />
        </div>
      </div>
    </section>
  );
};
